import { request } from '@@/plugin-request';

export const DEFAULT_OBJECT_STORAGE_BUCKET = 'savvyuni-intl-erp';
export const COS_PHYSICAL_BUCKET = 'savvyuni-intl-erp-1303037105';
export const COS_REGION = 'ap-beijing';

export async function getUploadParams(params) {
  return request('/api/materials/upload', {
    method: 'GET',
    params,
  });
}

export function isCosStorageBucket(bucket) {
  return bucket === DEFAULT_OBJECT_STORAGE_BUCKET || bucket === COS_PHYSICAL_BUCKET;
}

function getCosRegion(regionOrUploadParams) {
  if (!regionOrUploadParams) return COS_REGION;
  if (typeof regionOrUploadParams === 'string') return regionOrUploadParams;
  return regionOrUploadParams.region || regionOrUploadParams.Region || COS_REGION;
}

function compactFields(fields) {
  return Object.entries(fields).reduce((result, [key, value]) => {
    if (value !== undefined && value !== null) {
      result[key] = value;
    }
    return result;
  }, {});
}

function hasCosUploadParams(uploadParams = {}) {
  return (
    uploadParams.provider === 'cos' ||
    !!uploadParams.qAk ||
    !!uploadParams.qSignature ||
    !!uploadParams.qSignAlgorithm ||
    !!uploadParams.qKeyTime ||
    !!uploadParams['q-ak'] ||
    !!uploadParams['q-signature']
  );
}

function encodeObjectKey(key) {
  return String(key)
    .replace(/^\/+/, '')
    .split('/')
    .map((part) => {
      try {
        return encodeURIComponent(decodeURIComponent(part));
      } catch (error) {
        return encodeURIComponent(part);
      }
    })
    .join('/');
}

function stripBucketPrefix(key, bucket) {
  const bucketNames = [bucket, DEFAULT_OBJECT_STORAGE_BUCKET, COS_PHYSICAL_BUCKET].filter(Boolean);
  const matchedBucket = bucketNames.find((bucketName) => key.startsWith(`${bucketName}/`));
  return matchedBucket ? key.slice(matchedBucket.length + 1) : key;
}

function getObjectKeyFromStorageUrl(value, bucket) {
  if (!/^https?:\/\//i.test(String(value))) return value;

  try {
    const url = new URL(value);
    if (!url.hostname.includes('aliyuncs.com') && !url.hostname.includes('myqcloud.com')) {
      return value;
    }
    return stripBucketPrefix(url.pathname.replace(/^\/+/, ''), bucket);
  } catch (error) {
    return value;
  }
}

export function getObjectStorageAction(bucket = DEFAULT_OBJECT_STORAGE_BUCKET, uploadParams = {}) {
  if (isCosStorageBucket(bucket)) {
    return `https://${COS_PHYSICAL_BUCKET}.cos.${getCosRegion(uploadParams)}.myqcloud.com`;
  }
  return `https://${bucket}.oss-accelerate.aliyuncs.com`;
}

export function getObjectStorageFormData(
  uploadParams = {},
  key,
  bucket = DEFAULT_OBJECT_STORAGE_BUCKET,
) {
  if (isCosStorageBucket(bucket) && hasCosUploadParams(uploadParams)) {
    return compactFields({
      key,
      policy: uploadParams.policy,
      'q-sign-algorithm': uploadParams.qSignAlgorithm || uploadParams['q-sign-algorithm'],
      'q-ak': uploadParams.qAk || uploadParams['q-ak'],
      'q-sign-time': uploadParams.qSignTime || uploadParams.qKeyTime || uploadParams['q-sign-time'],
      'q-key-time': uploadParams.qKeyTime || uploadParams['q-key-time'],
      'q-signature': uploadParams.qSignature || uploadParams['q-signature'],
      'x-cos-security-token':
        uploadParams.securityToken || uploadParams.token || uploadParams['x-cos-security-token'],
    });
  }

  return compactFields({
    key,
    OSSAccessKeyId: uploadParams.accessId,
    policy: uploadParams.policy,
    Signature: uploadParams.signature,
  });
}

export function getObjectStorageUrl(
  bucket = DEFAULT_OBJECT_STORAGE_BUCKET,
  key,
  region = COS_REGION,
) {
  if (!bucket || !key) return undefined;
  const normalizedKey = getObjectKeyFromStorageUrl(key, bucket);
  if (/^https?:\/\//i.test(String(normalizedKey))) return normalizedKey;

  const encodedKey = encodeObjectKey(normalizedKey);
  if (!isCosStorageBucket(bucket)) {
    return `https://${bucket}.oss-accelerate.aliyuncs.com/${encodedKey}`;
  }
  return `https://${COS_PHYSICAL_BUCKET}.cos.${getCosRegion(region)}.myqcloud.com/${encodedKey}`;
}

export async function uploadObjectToStorage({
  bucket = DEFAULT_OBJECT_STORAGE_BUCKET,
  key,
  file,
  uploadParams,
  onProgress,
  signal,
}) {
  const formData = new FormData();
  const fields = getObjectStorageFormData(uploadParams, key, bucket);
  Object.entries(fields).forEach(([field, value]) => formData.append(field, value));
  formData.append('file', file);

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abortError = () => new DOMException('上传已取消', 'AbortError');
    const abort = () => {
      xhr.abort();
      reject(abortError());
    };
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    xhr.open('POST', getObjectStorageAction(bucket, uploadParams));
    signal?.addEventListener('abort', abort, { once: true });
    xhr.onloadend = () => signal?.removeEventListener('abort', abort);
    xhr.onabort = () => reject(abortError());
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded / event.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(formData);
  });

  return {
    bucket,
    name: key,
    url: getObjectStorageUrl(bucket, key, uploadParams?.region),
    res: {
      status: 200,
      requestUrls: [getObjectStorageUrl(bucket, key, uploadParams?.region)],
    },
  };
}

export async function download(data = {}) {
  if (data.key && (!data.bucket || isCosStorageBucket(data.bucket))) {
    return getObjectStorageUrl(data.bucket || DEFAULT_OBJECT_STORAGE_BUCKET, data.key, data.region);
  }

  return request('/api/materials/download', {
    method: 'POST',
    data,
  });
}
